// client/src/components/onboarding/BatchRecordForm.js
// ============================================
// BATCH RECORD FORM
// Single-row add/edit form for concierge onboarding records.
// Used by ConciergeOnboardingNew and ConciergeOnboardingDetail.
// ============================================

import React, { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

const BUSINESS_TYPES = [
  'restaurant',
  'cafe',
  'hotel',
  'catering',
  'retail',
  'food_service',
  'bakery',
  'bar',
  'other'
];

const EMPTY = {
  businessName: '',
  legalName: '',
  contactName: '',
  email: '',
  phone: '',
  address: '',
  vatNumber: '',
  taxOffice: '',
  businessType: 'restaurant',
  language: 'el'
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const BatchRecordForm = ({
  initialValue,
  onSubmit,
  onCancel,
  submitLabel,
  existingEmails = []
}) => {
  const { t } = useTranslation('admin');
  const uid = useId();
  const idFor = (field) => `${uid}-${field}`;
  const [form, setForm] = useState({ ...EMPTY, ...(initialValue || {}) });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setForm({ ...EMPTY, ...(initialValue || {}) });
    setErrors({});
  }, [initialValue]);

  const set = (field, v) => setForm((f) => ({ ...f, [field]: v }));

  const validate = () => {
    const next = {};
    if (!form.businessName?.trim()) next.businessName = t('conciergeOnboarding.validation.required', 'Required');
    if (!form.vatNumber?.trim()) next.vatNumber = t('conciergeOnboarding.validation.required', 'Required');
    if (!form.taxOffice?.trim()) next.taxOffice = t('conciergeOnboarding.validation.required', 'Required');
    if (!form.businessType) next.businessType = t('conciergeOnboarding.validation.required', 'Required');
    if (!form.email?.trim()) {
      next.email = t('conciergeOnboarding.validation.required', 'Required');
    } else if (!EMAIL_REGEX.test(form.email.trim())) {
      next.email = t('conciergeOnboarding.validation.invalidEmail', 'Invalid email address');
    } else if (existingEmails.map((e) => e.toLowerCase()).includes(form.email.trim().toLowerCase())) {
      next.email = t('conciergeOnboarding.validation.duplicateEmail', 'This email is already in the batch');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit?.({
      ...form,
      businessName: form.businessName.trim(),
      legalName: form.legalName?.trim() || undefined,
      email: form.email.trim().toLowerCase(),
      vatNumber: form.vatNumber.trim(),
      taxOffice: form.taxOffice.trim(),
      contactName: form.contactName?.trim() || undefined,
      phone: form.phone?.trim() || undefined,
      address: form.address?.trim() || undefined
    });
  };

  const labelFor = (key, fallback) =>
    t(`conciergeOnboarding.form.labels.${key}`, fallback);

  return (
    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
      <div className="md:col-span-2">
        <label htmlFor={idFor('businessName')} className="text-sm font-medium text-gray-700">{labelFor('businessName', 'Business name')}</label>
        <input
          id={idFor('businessName')}
          type="text"
          value={form.businessName}
          onChange={(e) => set('businessName', e.target.value)}
          className="mt-1 w-full rounded-md border-gray-300 px-3 py-2 border text-sm"
        />
        {errors.businessName && <p className="mt-1 text-xs text-red-600">{errors.businessName}</p>}
      </div>

      <div className="md:col-span-2">
        <label htmlFor={idFor('legalName')} className="text-sm font-medium text-gray-700">
          {labelFor('legalName', 'Legal name (Επωνυμία)')}
          <span className="ml-2 text-xs text-gray-500">{t('conciergeOnboarding.form.optional', '(optional — defaults to business name)')}</span>
        </label>
        <input
          id={idFor('legalName')}
          type="text"
          value={form.legalName}
          onChange={(e) => set('legalName', e.target.value)}
          className="mt-1 w-full rounded-md border-gray-300 px-3 py-2 border text-sm"
        />
      </div>

      <div>
        <label htmlFor={idFor('contactName')} className="text-sm font-medium text-gray-700">{labelFor('contactName', 'Contact name')}</label>
        <input
          id={idFor('contactName')}
          type="text"
          value={form.contactName}
          onChange={(e) => set('contactName', e.target.value)}
          className="mt-1 w-full rounded-md border-gray-300 px-3 py-2 border text-sm"
        />
      </div>

      <div>
        <label htmlFor={idFor('email')} className="text-sm font-medium text-gray-700">{labelFor('email', 'Email')}</label>
        <input
          id={idFor('email')}
          type="email"
          value={form.email}
          onChange={(e) => set('email', e.target.value)}
          className="mt-1 w-full rounded-md border-gray-300 px-3 py-2 border text-sm"
        />
        {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
      </div>

      <div>
        <label htmlFor={idFor('phone')} className="text-sm font-medium text-gray-700">{labelFor('phone', 'Phone')}</label>
        <input
          id={idFor('phone')}
          type="text"
          value={form.phone}
          onChange={(e) => set('phone', e.target.value)}
          className="mt-1 w-full rounded-md border-gray-300 px-3 py-2 border text-sm"
        />
      </div>

      <div>
        <label htmlFor={idFor('vatNumber')} className="text-sm font-medium text-gray-700">{labelFor('vatNumber', 'VAT number')}</label>
        <input
          id={idFor('vatNumber')}
          type="text"
          value={form.vatNumber}
          onChange={(e) => set('vatNumber', e.target.value)}
          className="mt-1 w-full rounded-md border-gray-300 px-3 py-2 border text-sm"
        />
        {errors.vatNumber && <p className="mt-1 text-xs text-red-600">{errors.vatNumber}</p>}
      </div>

      <div>
        <label htmlFor={idFor('taxOffice')} className="text-sm font-medium text-gray-700">{labelFor('taxOffice', 'Tax office')}</label>
        <input
          id={idFor('taxOffice')}
          type="text"
          value={form.taxOffice}
          onChange={(e) => set('taxOffice', e.target.value)}
          className="mt-1 w-full rounded-md border-gray-300 px-3 py-2 border text-sm"
        />
        {errors.taxOffice && <p className="mt-1 text-xs text-red-600">{errors.taxOffice}</p>}
      </div>

      <div>
        <label htmlFor={idFor('businessType')} className="text-sm font-medium text-gray-700">{labelFor('businessType', 'Business type')}</label>
        <select
          id={idFor('businessType')}
          value={form.businessType}
          onChange={(e) => set('businessType', e.target.value)}
          className="mt-1 w-full rounded-md border-gray-300 px-3 py-2 border text-sm"
        >
          {BUSINESS_TYPES.map((bt) => (
            <option key={bt} value={bt}>
              {t(`conciergeOnboarding.form.businessTypes.${bt}`, bt)}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor={idFor('language')} className="text-sm font-medium text-gray-700">{labelFor('language', 'Email language')}</label>
        <select
          id={idFor('language')}
          value={form.language}
          onChange={(e) => set('language', e.target.value)}
          className="mt-1 w-full rounded-md border-gray-300 px-3 py-2 border text-sm"
        >
          <option value="el">Ελληνικά</option>
          <option value="en">English</option>
        </select>
      </div>

      <div className="md:col-span-2">
        <label htmlFor={idFor('address')} className="text-sm font-medium text-gray-700">{labelFor('address', 'Address')}</label>
        <input
          id={idFor('address')}
          type="text"
          value={form.address}
          onChange={(e) => set('address', e.target.value)}
          className="mt-1 w-full rounded-md border-gray-300 px-3 py-2 border text-sm"
        />
      </div>

      <div className="md:col-span-2 flex justify-end gap-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {t('conciergeOnboarding.form.cancel', 'Cancel')}
          </button>
        )}
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
        >
          {submitLabel || t('conciergeOnboarding.form.addRecord', 'Add record')}
        </button>
      </div>
    </form>
  );
};

export default BatchRecordForm;
